
import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";
import { Club, ClubFormValues } from "@/types/clubs";

// Export these types for use in other components
export type { Club, ClubFormValues } from "@/types/clubs";

export type ClubsData = {
  clubs: Club[];
  loading: boolean;
  error: string;
  totalCount: number;
}

export const useClubs = () => {
  const [clubs, setClubs] = useState<Club[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [joiningClubId, setJoiningClubId] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchClubs = async () => {
    try {
      setLoading(true);
      setError("");

      // Get the clubs count first
      const { data, error: countError } = await supabase
        .from('clubs')
        .select('*');
      
      if (countError) throw countError;
      setTotalCount(data?.length || 0);

      // Get clubs data
      const { data: clubsData, error } = await supabase
        .from("clubs")
        .select("*, club_members(user_id)");

      if (error) throw error;

      if (clubsData) {
        // Get the current user to check club membership
        const { data: { session } } = await supabase.auth.getSession();
        
        // Process clubs data to add is_member flag
        const processedClubs = clubsData.map(club => {
          const memberCount = Array.isArray(club.club_members) ? club.club_members.length : 0;
          const isMember = session?.user ? 
            Array.isArray(club.club_members) && 
            club.club_members.some((member: any) => member.user_id === session.user.id) : 
            false;
          
          return {
            ...club,
            member_count: memberCount,
            is_member: isMember
          };
        });

        setClubs(processedClubs);
      }
    } catch (err: any) {
      setError(err.message);
      toast({
        title: "Error",
        description: "Failed to load clubs",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleJoinClub = async (clubId: string, isCurrentlyMember: boolean, clubName?: string) => {
    try {
      setJoiningClubId(clubId);
      
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        toast({
          title: "Authentication required",
          description: "Please sign in to join clubs",
          variant: "destructive",
        });
        return;
      }
      
      if (isCurrentlyMember) {
        // Leave the club
        const { error } = await supabase
          .from("club_members")
          .delete()
          .eq("club_id", clubId)
          .eq("user_id", session.user.id);
        
        if (error) throw error;
        
        toast({
          title: "Left club",
          description: `You are no longer a member of ${clubName || 'this club'}`,
        });
      } else {
        // Join the club
        const { error } = await supabase
          .from("club_members")
          .insert({
            club_id: clubId,
            user_id: session.user.id,
          });
        
        if (error) throw error;
        
        toast({
          title: "Joined club",
          description: `You are now a member of ${clubName || 'this club'}`,
        });
      }
      
      // Refresh clubs to update membership status
      await fetchClubs();
      
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to update club membership",
        variant: "destructive",
      });
    } finally {
      setJoiningClubId(null);
    }
  };

  const createClub = async (values: ClubFormValues): Promise<boolean> => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        toast({
          title: "Authentication required",
          description: "Please sign in to create a club",
          variant: "destructive",
        });
        return false;
      }
      
      const { error } = await supabase
        .from("clubs")
        .insert({
          name: values.name,
          description: values.description,
          category: values.category,
          leader_id: session.user.id
        });
      
      if (error) throw error;
      
      // Also make the creator a member of the club
      await fetchClubs();
      
      toast({
        title: "Club created",
        description: "Your new club has been created successfully",
      });
      
      return true;
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to create club",
        variant: "destructive",
      });
      return false;
    }
  };

  const getUniqueCategories = (): string[] => {
    const categories = clubs.map(club => club.category).filter(Boolean) as string[];
    return [...new Set(categories)];
  };

  const filterClubs = (searchQuery: string): Club[] => {
    if (!searchQuery && !selectedCategory) return clubs;
    
    return clubs.filter(club => {
      const matchesSearch = !searchQuery || 
        club.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (club.description && club.description.toLowerCase().includes(searchQuery.toLowerCase()));
        
      const matchesCategory = !selectedCategory || club.category === selectedCategory;
      
      return matchesSearch && matchesCategory;
    });
  };

  useEffect(() => {
    fetchClubs();
  }, []);

  return { 
    clubs, 
    loading, 
    error, 
    totalCount, 
    selectedCategory,
    setSelectedCategory,
    joiningClubId,
    handleJoinClub,
    createClub,
    getUniqueCategories,
    filterClubs
  };
};
